import {z} from 'zod'
import {booking} from '@whyhotel/common-schema'

export const SearchParamsSchema = z.object({
  location: z.string(),
  dateRange: booking.SearchDateRangeParamsSchema,
  guests: z.number(),
  propertyId: z.string().optional(),
  specialRates: booking.SearchSpecialRatesParamsSchema
})

export const LowestRateSchema = z.object({
  total: z.number(),
  nightly: z.number(),
  currency: z.string()
})

export const MapCoordinatesSchema = z.object({
  latitude: z.number(),
  longitude: z.number()
})

export const ImageAssetSchema = z.object({
  image: z.string(),
  altText: z.string()
})

export const PropertyConfigSchema = z.object({
  propertyApaleoId: z.string(),
  name: z.string(),
  shortName: z.string(),
  phone: z.string(),
  description: z.string(),
  neighborhood: z.string(),
  location: z.string(),
  regions: z.array(z.string()),
  timeZone: z.string(),
  isEnabled: z.boolean(),
  isPopup: z.boolean(),
  allowMonthlyRates: z.boolean(),
  allowHiltonBookings: z.boolean(),
  hiltonBookingUrl: z.string().optional(),
  hiltonBookingPointsMessage: z.string().optional(),
  images: z.array(ImageAssetSchema),
  features: z.array(z.string()),
  highlightedFeatures: z.array(z.string()),
  address: z.object({
    addressLine1: z.string(),
    addressLine2: z.string().optional(),
    postalCode: z.string(),
    city: z.string(),
    state: z.string(),
    countryCode: z.string()
  }),
  coordinates: MapCoordinatesSchema,
  openingDate: z.string().optional(),
  closingDate: z.string().optional()
})

export const UnitGroupConfigSchema = z.object({
  unitgroupApaleoId: z.string(),
  propertyApaleoId: z.string(),
  name: z.string(),
  description: z.string(),
  images: z.array(ImageAssetSchema),
  attributes: z.object({
    bedrooms: z.number(),
    bathrooms: z.number(),
    maxGuests: z.number(),
    unitSize: z.string().optional()
  }),
  highlightedFeatures: z.array(z.string()),
  basicFeatures: z.array(z.string()),
  sleepingFeatures: z.array(z.string()),
  diningFeatures: z.array(z.string()),
  workingFeatures: z.array(z.string()),
  familyFeatures: z.array(z.string()),
  outdoorFeatures: z.array(z.string()),
  safetyFeatures: z.array(z.string()),
  accessibilityFeatures: z.array(z.string())
})

export const IbeConfigSchema = z.object({
  locations: z.array(z.string()),
  properties: z.array(PropertyConfigSchema),
  regions: z.array(z.string()),
  unitGroups: z.array(UnitGroupConfigSchema)
})

export const ToastNotificationSchema = z.object({
  id: z.string(),
  message: z.string(),
  type: z.enum(['add', 'remove'])
})

export const ShoppingCartItemSchema = z.object({
  id: z.string(),
  propertyId: z.string(),
  offer: booking.BookingOfferSchema,
  additionalServices: z.array(booking.ServiceOfferSchema),
  guests: z.number()
})

export const ItemEditorModeSchema = z.enum(['Add', 'Edit'])

export const ItemEditSchema = z.object({
  item: ShoppingCartItemSchema,
  mode: ItemEditorModeSchema
})

export const ShoppingCartValidationErrorSchema = z.object({
  id: z.string(),
  code: z.enum([
    'not_available',
    'price_increase',
    'price_decrease',
    'invalid_arrival_date',
    'unit_group_unavailable'
  ]),
  message: z.string()
})

export const ShoppingCartValidationResultSchema = z.object({
  latestOffers: z.array(booking.BookingOfferSchema),
  validationErrors: z.array(ShoppingCartValidationErrorSchema),
  lastValidated: z.union([z.string().datetime(), z.null()]).optional()
})

export const ShoppingCartValidationSchema = z.object({
  isCartValidationOpen: z.boolean(),
  latestOffers: z.array(booking.BookingOfferSchema),
  validationErrors: z.array(ShoppingCartValidationErrorSchema),
  lastValidated: z.union([z.string().datetime(), z.null()]).optional()
})

export const ShoppingCartSchema = z.object({
  items: z.array(ShoppingCartItemSchema),
  isCartOpen: z.boolean(),
  isItemEditorOpen: z.boolean(),
  itemEditorMode: ItemEditorModeSchema,
  itemToEdit: z.union([ShoppingCartItemSchema, z.null()]).optional()
})

export const PaymentSessionRequestSchema = z.object({
  amountInCents: z.number(),
  isPaymentAccount: z.boolean(),
  metadata: booking.CreateBookingMetadataSchema,
  propertyId: z.string(),
  returnUrl: z.string(),
  shopperId: z.string().optional(),
  shopperEmail: z.string(),
  lastUpdated: z.union([z.string(), z.null()]).optional()
})

export const PaymentSessionSchema = z.object({
  id: z.string(),
  paymentSessionId: z.string(),
  sessionData: z.string()
})

export const HubspotPropertySchema = z.object({
  id: z.number().optional(),
  property_id: z.string().optional(),
  pm_property_name: z.string().optional(),
  city_page_url_slug: z.string().optional(),
  location_group: z.string().optional()
})

export const HubspotPropertyResultsSchema = z.object({
  hasMore: z.boolean(),
  offset: z.number(),
  results: z.array(HubspotPropertySchema),
  total: z.number()
})

export const HubspotLocationSchema = z.object({
  name: z.string(),
  url: z.string()
})

export const UserSchema = z.object({
  isAuthenticated: z.boolean(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional()
})

export const GuestInfoSchema = z.object({
  firstName: z.string().min(1, 'First Name is required'),
  lastName: z.string().min(1, 'Last Name is required'),
  phone: z.string().min(7, 'Mobile Phone is required'),
  formattedPhone: z.string().optional(),
  email: z.string().min(1, 'Email is required').email(),
  comments: z.string().optional(),
  acceptTerms: z.boolean().refine(value => value === true),
  lastUpdated: z.union([z.string(), z.null()]).optional()
})

export const PaymentStatusSchema = z.enum([
  'Inactive',
  'Processing',
  'Success',
  'Failure'
])

export const PaymentInfoSchema = z.object({
  paymentSessionId: z.string().nullable().optional(),
  status: PaymentStatusSchema,
  error: z.string().optional()
})

// BookingInfoSchema - Frontend uses 'status' instead of 'bookingStatus'
export const BookingInfoSchema = z.object({
  bookingId: z.string().nullable().optional(),
  status: booking.BookingStatusSchema
})

// -----------------------------------------------------------------------------
//  Enums
// -----------------------------------------------------------------------------

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR'
}

export enum AppView {
  HOME = 'Home',
  CHOOSE_PROPERTY = 'ChooseProperty',
  CHOOSE_UNIT = 'ChooseUnit',
  CHECKOUT = 'Checkout',
  CONFIRMATION = 'Confirmation'
}

export enum PaymentSessionStatus {
  PaymentSessionCreated = 'PaymentSessionCreated',
  PaymentRejected = 'PaymentRejected',
  PaymentProcessing = 'PaymentProcessing',
  PaymentSucceeded = 'PaymentSucceeded',
  PaymentFailed = 'PaymentFailed'
}

export const BookingStatusValues = booking.BookingStatusSchema.Enum

export const PaymentStatusValues = PaymentStatusSchema.Enum

// -----------------------------------------------------------------------------
//  Types - Inferred from schemas
// -----------------------------------------------------------------------------

// Frontend-specific types
export type BookingInfo = z.infer<typeof BookingInfoSchema>
export type BookingStatus = z.infer<typeof booking.BookingStatusSchema>
export type GuestInfo = z.infer<typeof GuestInfoSchema>
export type HubspotLocation = z.infer<typeof HubspotLocationSchema>
export type HubspotProperty = z.infer<typeof HubspotPropertySchema>
export type HubspotPropertyResults = z.infer<
  typeof HubspotPropertyResultsSchema
>
export type IbeConfig = z.infer<typeof IbeConfigSchema>
export type ImageAsset = z.infer<typeof ImageAssetSchema>
export type ItemEditorMode = z.infer<typeof ItemEditorModeSchema>
export type ItemEdit = z.infer<typeof ItemEditSchema>
export type LowestRate = z.infer<typeof LowestRateSchema>
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>
export type PaymentInfo = z.infer<typeof PaymentInfoSchema>
export type PaymentSessionRequest = z.infer<typeof PaymentSessionRequestSchema>
export type PaymentSession = z.infer<typeof PaymentSessionSchema>
export type PropertyConfig = z.infer<typeof PropertyConfigSchema>
export type SearchParams = z.infer<typeof SearchParamsSchema>
export type ShoppingCart = z.infer<typeof ShoppingCartSchema>
export type ShoppingCartItem = z.infer<typeof ShoppingCartItemSchema>
export type ShoppingCartValidation = z.infer<
  typeof ShoppingCartValidationSchema
>
export type ShoppingCartValidationError = z.infer<
  typeof ShoppingCartValidationErrorSchema
>
export type ShoppingCartValidationResult = z.infer<
  typeof ShoppingCartValidationResultSchema
>
export type SpecialRatePlans = z.infer<typeof booking.SpecialRatePlansSchema>
export type ToastNotification = z.infer<typeof ToastNotificationSchema>
export type User = z.infer<typeof UserSchema>
